/**
 * MarketingCampaignExpenses.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Painel de gastos de marketing por campanha (Playbook v1.3)
 *
 * Dados: GET /api/governance/finance/planning/expenses/marketing-campaigns
 * Integrado em: GovernanceFinancePage → aba Despesas (sub-seção Marketing)
 *
 * Features:
 *   - Tabela campanha × mês com totais BRL
 *   - Filtro por mês
 *   - Badge UTM source (linkedin / instagram)
 *   - Indicador quando campanha tem draft aprovado (content_drafts)
 *   - Total anual por campanha
 *   - Estado vazio com instrução de como registrar gastos vinculados
 * ─────────────────────────────────────────────────────────────────────────────
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Tag, TrendingDown, RefreshCw, ChevronDown, ChevronUp, ExternalLink, Info } from 'lucide-react';
import { AZURE } from '../../services/api';

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD  = '#b48c2c';

const fmt = (v) => {
  if (!v && v !== 0) return '—';
  return 'R$ ' + Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const MONTH_LABELS = {
  '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr',
  '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Ago',
  '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez',
};

const UTM_COLORS = {
  linkedin:  { bg: '#EFF6FF', text: '#1D4ED8', label: 'LinkedIn' },
  instagram: { bg: '#FDF4FF', text: '#7C3AED', label: 'Instagram' },
  google:    { bg: '#FEF9C3', text: '#854D0E', label: 'Google'    },
  meta:      { bg: '#EFF6FF', text: '#1D4ED8', label: 'Meta'      },
  email:     { bg: '#F0FDF4', text: '#15803D', label: 'E-mail'    },
};

const UtmBadge = ({ source }) => {
  if (!source) return null;
  const cfg = UTM_COLORS[source.toLowerCase()] || { bg: '#F3F4F6', text: '#374151', label: source };
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold"
      style={{ background: cfg.bg, color: cfg.text }}>
      {cfg.label}
    </span>
  );
};

const MediumBadge = ({ medium }) => {
  if (!medium) return null;
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
      {medium}
    </span>
  );
};

/** Converte 'vukz_launch_2026_06' → 'Vukz Launch — Jun/2026' */
const fmtCampaignName = (c) => {
  if (!c) return '—';
  // Tenta extrair YYYY_MM do final
  const m = c.match(/^(.+?)_(\d{4})_(\d{2})$/);
  if (m) {
    const label = m[1].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const month = MONTH_LABELS[m[3]] || m[3];
    return `${label} — ${month}/${m[2]}`;
  }
  return c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// ─── Main Component ──────────────────────────────────────────────────────────
const MarketingCampaignExpenses = () => {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [monthFilter, setMonthFilter] = useState('all');
  const [sortBy, setSortBy]     = useState('total_brl'); // total_brl | campaign | reference_month
  const [sortDir, setSortDir]   = useState('desc');
  const [expanded, setExpanded] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await AZURE.get('/governance/finance/planning/expenses/marketing-campaigns');
      setData(resp.data?.data || null);
    } catch (e) {
      setError('Não foi possível carregar os dados de campanhas.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Filtering ──────────────────────────────────────────────
  const allMonths = data?.meta?.months_present || [];

  const rows = (data?.campaigns || []).filter(r =>
    monthFilter === 'all' || r.reference_month === monthFilter
  );

  // ── Sorting ────────────────────────────────────────────────
  const sorted = [...rows].sort((a, b) => {
    let va = a[sortBy], vb = b[sortBy];
    if (typeof va === 'string') va = va.toLowerCase();
    if (typeof vb === 'string') vb = vb.toLowerCase();
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <span className="text-gray-300 ml-0.5">↕</span>;
    return sortDir === 'asc' ? <ChevronUp size={11} className="inline ml-0.5" /> : <ChevronDown size={11} className="inline ml-0.5" />;
  };

  const totalBrl = rows.reduce((s, r) => s + (r.total_brl || 0), 0);
  const totalTxns = rows.reduce((s, r) => s + (r.transaction_count || 0), 0);

  // ── Empty / Fallback ───────────────────────────────────────
  const isFallback = data?._fallback;
  const isEmpty = !loading && !error && sorted.length === 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 cursor-pointer select-none"
        onClick={() => setExpanded(e => !e)}>
        <div className="flex items-center gap-2">
          <Tag size={15} style={{ color: BRAND_GREEN }} />
          <span className="font-bold text-gray-900 text-sm">Gastos por Campanha (Marketing Playbook)</span>
          {data?.meta?.total_campaigns > 0 && (
            <span className="text-[10px] font-semibold bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
              {data.meta.total_campaigns} campanhas
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!loading && data?.meta?.total_brl > 0 && (
            <span className="text-sm font-bold text-red-600">{fmt(data.meta.total_brl)}</span>
          )}
          <button onClick={(e) => { e.stopPropagation(); load(); }}
            className="p-1 hover:bg-gray-100 rounded" title="Atualizar">
            <RefreshCw size={12} className={loading ? 'animate-spin text-gray-400' : 'text-gray-400'} />
          </button>
          {expanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="p-4 space-y-3">
          {/* Loading */}
          {loading && (
            <div className="space-y-2">
              {[1,2,3].map(i => <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />)}
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="text-xs text-red-600 bg-red-50 rounded px-3 py-2">{error}</div>
          )}

          {/* Fallback info */}
          {isFallback && !loading && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded px-3 py-2">
              <Info size={13} className="mt-0.5 shrink-0" />
              <span>Migration 312 ainda não foi aplicada ao banco. Execute <code className="bg-amber-100 px-1 rounded">_apply_312.py --apply</code> para habilitar esta funcionalidade.</span>
            </div>
          )}

          {/* Filters */}
          {!loading && !error && !isFallback && (
            <div className="flex items-center gap-2 flex-wrap">
              <select value={monthFilter} onChange={e => setMonthFilter(e.target.value)}
                className="text-xs border border-gray-200 rounded px-2 py-1">
                <option value="all">Todos os meses</option>
                {allMonths.map(m => (
                  <option key={m} value={m}>
                    {MONTH_LABELS[m?.slice(5)] || m} {m?.slice(0, 4)}
                  </option>
                ))}
              </select>
              {monthFilter !== 'all' && (
                <button onClick={() => setMonthFilter('all')}
                  className="text-xs text-gray-500 hover:text-gray-700 underline">Limpar</button>
              )}
              {/* Summary chips */}
              {totalBrl > 0 && (
                <>
                  <span className="ml-auto text-xs text-gray-500">{totalTxns} lançamentos</span>
                  <span className="text-xs font-bold text-red-600">{fmt(totalBrl)}</span>
                </>
              )}
            </div>
          )}

          {/* Empty state */}
          {isEmpty && !isFallback && (
            <div className="text-center py-6 space-y-2">
              <Tag size={24} className="mx-auto text-gray-300" />
              <p className="text-sm text-gray-500">Nenhum gasto de marketing vinculado a campanhas ainda.</p>
              <p className="text-xs text-gray-400">
                Para vincular, cadastre despesas com <code className="bg-gray-100 px-1 rounded">category=marketing</code> e preencha o campo <code className="bg-gray-100 px-1 rounded">campaign</code> com a mesma convenção do Playbook (ex: <em>vukz_launch_2026_06</em>).
              </p>
            </div>
          )}

          {/* Table */}
          {!loading && !error && sorted.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600 min-w-[180px] cursor-pointer select-none"
                      onClick={() => toggleSort('campaign')}>
                      Campanha <SortIcon col="campaign" />
                    </th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-600 cursor-pointer"
                      onClick={() => toggleSort('reference_month')}>
                      Mês <SortIcon col="reference_month" />
                    </th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-600">Canal</th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-600">Subcategoria</th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-600">Centro</th>
                    <th className="px-2 py-2 text-right font-semibold text-gray-600 cursor-pointer"
                      onClick={() => toggleSort('total_brl')}>
                      Valor BRL <SortIcon col="total_brl" />
                    </th>
                    <th className="px-2 py-2 text-center font-semibold text-gray-600">Draft</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((row, idx) => (
                    <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                      {/* Campaign name */}
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {fmtCampaignName(row.campaign)}
                      </td>
                      {/* Month */}
                      <td className="px-2 py-2 text-gray-600 whitespace-nowrap">
                        {row.reference_month
                          ? `${MONTH_LABELS[row.reference_month?.slice(5)] || ''} ${row.reference_month?.slice(0,4)}`
                          : '—'}
                      </td>
                      {/* UTM Source + Medium */}
                      <td className="px-2 py-2">
                        <div className="flex flex-wrap gap-1">
                          <UtmBadge source={row.utm_source} />
                          <MediumBadge medium={row.utm_medium} />
                        </div>
                      </td>
                      {/* Subcategory */}
                      <td className="px-2 py-2 text-gray-500">{row.subcategory || '—'}</td>
                      {/* Cost center */}
                      <td className="px-2 py-2 text-gray-500 font-mono text-[11px]">
                        {row.cost_center_code || '—'}
                      </td>
                      {/* Amount */}
                      <td className="px-2 py-2 text-right font-bold text-red-600">
                        {fmt(row.total_brl)}
                        {row.transaction_count > 1 && (
                          <div className="text-[10px] text-gray-400 font-normal">{row.transaction_count} lançamentos</div>
                        )}
                      </td>
                      {/* Draft status indicator */}
                      <td className="px-2 py-2 text-center">
                        {row.draft_status ? (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                            row.draft_status === 'published' ? 'bg-green-100 text-green-700' :
                            row.draft_status === 'approved'  ? 'bg-blue-100 text-blue-700'  :
                            row.draft_status === 'pending'   ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {row.draft_status === 'published' ? '✓ pub' :
                             row.draft_status === 'approved'  ? '✓ apr' :
                             row.draft_status === 'pending'   ? 'pendente' : row.draft_status}
                          </span>
                        ) : (
                          <span className="text-gray-300 text-[10px]">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {/* Footer totals */}
                {sorted.length > 1 && (
                  <tfoot>
                    <tr className="border-t-2 border-gray-300 bg-gray-50">
                      <td className="px-3 py-2 font-bold text-gray-700" colSpan={5}>
                        TOTAL ({sorted.length} registros)
                      </td>
                      <td className="px-2 py-2 text-right font-bold text-red-700">{fmt(totalBrl)}</td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          {/* Footer note */}
          {!loading && !isFallback && (
            <p className="text-[10px] text-gray-400 mt-1">
              Dados de <code>financial_expenses</code> onde <code>category=marketing</code> e <code>campaign IS NOT NULL</code>.
              Playbook version e status do draft vêm de <code>content_drafts</code> via campo <code>campaign</code>.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default MarketingCampaignExpenses;
